"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  WEBINAR_FIELDS as F,
  formatWebinar,
  type WebinarKind,
  type WebinarLabels,
  webinarIsReady,
} from "@/lib/tool-kit/media-sources/webinar-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const WEBINAR_CITATION_GENERATOR_STRINGS = {
  formHeading: "Webinar details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the webinar's title and its presenter or host to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "Which version did you use?",
  kindRecording: "A recording I watched",
  kindLive: "A live session I attended",
  presentersLabel: "Presenter(s)",
  presentersPlaceholder: "Angela Gibson",
  presentersHint: "One per line, as First Last or Last, First.",
  titleLabel: "Webinar title",
  titlePlaceholder: "MLA style 101",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  hostLabel: "Host organization",
  hostPlaceholder: "Modern Language Association",
  hostHint:
    "The organization that ran the webinar. APA uses it as the publisher.",
  dateLabel: "Date of the webinar",
  datePlaceholder: "YYYY-MM-DD",
  postedDateLabel: "Date the recording was posted (optional)",
  postedDatePlaceholder: "YYYY-MM-DD",
  postedDateHint:
    "If it differs from the live date. MLA dates a recording by this date; Chicago gives both.",
  moderatorLabel: "Moderator (optional)",
  moderatorPlaceholder: "Andrew McCall",
  platformLabel: "Site hosting the recording (optional)",
  platformPlaceholder: "YouTube",
  platformHint:
    "Only if the recording sits on a site other than the host's own, such as YouTube or Vimeo.",
  durationLabel: "Running time (optional, Chicago)",
  durationPlaceholder: "1:01:45",
  urlLabel: "URL of the recording",
  urlPlaceholder: "https://outreach.mla.org/mla-style",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 (§10.12) cites a recorded webinar with the [Webinar] label and the host organization as the publisher. When you quote, add the time stamp where the quotation starts to the in-text citation.",
  apaLiveNote:
    "APA 7 (§8.9) treats a webinar that was not recorded as a personal communication: cite it in the text only, with the presenter's initials and the exact date, and add no reference list entry.",
  mlaNote:
    "MLA 9 sets a webinar's title like a course title, without italics or quotation marks. A live session ends with Webinar; a recording is dated by its posting and ends with its URL.",
  chicagoNote:
    "The Chicago Manual has no webinar example. This follows its Quick Guide sample for a recorded virtual lecture: title in quotation marks, the event date, then the posting date and who posted it.",
  exampleRecording: {
    presenters: "Angela Gibson",
    title: "MLA style 101",
    host: "Modern Language Association",
    date: "2017-08-22",
    postedDate: "2017-08-30",
    url: "https://outreach.mla.org/mla-style",
  },
  exampleLive: {
    presenters: "Angela Gibson",
    title: "MLA style 101",
    host: "Modern Language Association",
    date: "2017-08-22",
  },
} as const;

type Strings = typeof WEBINAR_CITATION_GENERATOR_STRINGS;

function sharedFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.presenters,
      kind: "people",
      label: strings.presentersLabel,
      placeholder: strings.presentersPlaceholder,
      hint: strings.presentersHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.host,
      kind: "text",
      label: strings.hostLabel,
      placeholder: strings.hostPlaceholder,
      hint: strings.hostHint,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.moderator,
      kind: "text",
      label: strings.moderatorLabel,
      placeholder: strings.moderatorPlaceholder,
    },
  ];
}

function recordingFields(strings: Strings): SourceField[] {
  return [
    ...sharedFields(strings),
    {
      id: F.postedDate,
      kind: "date",
      label: strings.postedDateLabel,
      placeholder: strings.postedDatePlaceholder,
      hint: strings.postedDateHint,
    },
    {
      id: F.platform,
      kind: "text",
      label: strings.platformLabel,
      placeholder: strings.platformPlaceholder,
      hint: strings.platformHint,
    },
    {
      id: F.duration,
      kind: "text",
      label: strings.durationLabel,
      placeholder: strings.durationPlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function webinarLabels(strings: Strings): WebinarLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    apaLiveNote: strings.apaLiveNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function WebinarCitationGeneratorWidget() {
  const strings = useToolUi(WEBINAR_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<WebinarKind>("recording");
  const fields = useMemo(
    () =>
      kind === "recording" ? recordingFields(strings) : sharedFields(strings),
    [kind, strings]
  );
  const labels = useMemo(() => webinarLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatWebinar(kind, values, labels),
    [kind, labels]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "recording" ? strings.exampleRecording : strings.exampleLive
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="webinar-kind"
          onChange={setKind}
          options={[
            { value: "recording", label: strings.kindRecording },
            { value: "live", label: strings.kindLive },
          ]}
          value={kind}
        />
      }
      isReady={webinarIsReady}
      strings={strings}
    />
  );
}
